'use client';

import React, { useEffect, useRef, useState } from 'react';

function CheckIcon({ gold = false }: { gold?: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`w-5 h-5 flex-shrink-0 mt-0.5 text-[#b5973a]`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

export default function ServicesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const tuneUpItems = [
    'Calibrate Thermostat',
    'Clean Air Filter',
    'Inspection Replace Customer Supplied Air Filter',
    'Clean Blower Components',
    'Check Air Flow',
    'Test Electrical Connections',
    'Installation & Area Around the Unit (include clearances)',
    'Inspect Unit Condition',
    'Test Safety Controls',
    'Inspect Condensate Drains',
    'Test Volts and Amps',
  ];

  return (
    <section
      id="events"
      ref={ref}
      className="relative py-20"
      style={{
        background: `url('/assets/images/gorgis_team_services_bg.png') center center / cover no-repeat`,
      }}
    >
      <div className="absolute inset-0 bg-[#1e1e1e]/85" />

      <div className="relative z-10 container mx-auto px-4">
        <div className="section-title pb-12">
          <h2>Services</h2>
          <p>Get A Better Solution with San Diego HVAC Service</p>
        </div>

        {/* Free Estimate & Second Opinion */}
        <div
          className={`grid lg:grid-cols-2 gap-8 mb-12 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="bg-[#252525]/80 border border-[#b5973a]/30 rounded-xl p-8 hover:border-[#b5973a] transition-colors duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#b5973a]/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#b5973a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="font-serif text-[#b5973a] text-2xl font-semibold">Free Estimate</h3>
            </div>
            <p className="text-[#ddd] italic leading-relaxed">
              When you are ready to install a new furnace, central air conditioner or other type of whole-house HVAC unit, schedule for a free estimate at-home consultation with Gorgis Air Conditioning and Heating. We&apos;ll explain your options and discuss your opinion so you can choose the HVAC unit that&apos;s right for your house and budget. All estimates are obligation free, no pressure, and 100% guaranteed.
            </p>
          </div>

          <div className="bg-[#252525]/80 border border-[#b5973a]/30 rounded-xl p-8 hover:border-[#b5973a] transition-colors duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#b5973a]/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#b5973a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="font-serif text-[#b5973a] text-2xl font-semibold">Free Second Opinion</h3>
            </div>
            <p className="text-[#ddd] italic leading-relaxed">
              For the second opinion, after we give you multiple options, you will discuss it with your family and friends. All of that is free and we are happy to do that for you at no charge.
            </p>
          </div>
        </div>

        {/* Tune-Up & Others */}
        <div
          className={`grid lg:grid-cols-2 gap-8 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="bg-[#252525]/80 border border-[#b5973a]/30 rounded-xl p-8 hover:border-[#b5973a] transition-colors duration-300">
            <h3 className="font-serif text-[#b5973a] text-2xl font-semibold mb-6">Tune-Up 38 Point Inspection</h3>
            <ul className="space-y-2">
              {tuneUpItems.map((item) => (
                <li key={item} className="flex items-start gap-2 text-[#ddd]">
                  <CheckIcon />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#252525]/80 border border-[#b5973a]/30 rounded-xl p-8 hover:border-[#b5973a] transition-colors duration-300">
            <h3 className="font-serif text-[#b5973a] text-2xl font-semibold mb-6">Others</h3>
            <p className="text-[#ddd] italic leading-relaxed mb-8">
              Gorgis Air Conditioning and Heating has a team of factory-trained service technicians who stay up to date on the latest technological advances in heating and air conditioning service in San Diego. We are a family-owned and operated HVAC company with over 20 years of combined experience with all makes and models of furnaces, heat pumps, and mini-split systems. Call now to schedule. We offer same day service and 100% satisfaction guarantee.
            </p>
            <div className="bg-[#b5973a]/15 border border-[#b5973a]/50 rounded-lg p-6 text-center">
              <p className="text-[#b5973a] font-poppins font-semibold text-lg mb-2">Ready to Schedule?</p>
              <p className="text-white/70 text-sm mb-4">San Diego: 619-277-3287 or 619-244-6693</p>
              <a
                href="https://book.housecallpro.com/book/Gorgis-Air-conditioning-and-heating/8d64616590bd4fe49c1a93f43f1cbfae"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#b5973a] hover:bg-[#d4aa4a] text-white font-poppins font-bold uppercase tracking-wider px-6 py-3 rounded-full text-sm transition-all duration-300 shadow-lg shadow-[#b5973a]/30"
              >
                Book Online Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
